// SPIN CYCLE (§6.5, §12.5): a washing-machine drum floor over churning soap foam. The floor is a
// perforated steel disc (hex grid of holes, pressed rings, a hazard-striped edge) inside a steel
// rim with red warning domes; the floor shader leaves wet soap streaks behind each sweeper arm. The
// lint-brush arms (aqua core, instanced bristles that bend toward the trailing side with speed) and
// the agitator hub turn with `view.hazard`; the Sudden Death third arm is announced by a 1-bar
// shadow on the floor and drops in from above; reversals flash the red domes 2 beats ahead. Soap
// bubbles are iridescent fresnel spheres that wobble before they pop into droplets and a ring.
// Underneath hovers the drum back: a perforated chrome tub with a three-arm spider and the shaft
// dipping into the suds; behind, a giant porthole door, floating socks and a rubber duck.
import {
  CatmullRomCurve3, Color, CylinderGeometry, DynamicDrawUsage, Float32BufferAttribute, Group, InstancedBufferAttribute, InstancedMesh, Matrix4, Mesh, PlaneGeometry,
  Quaternion, ShaderMaterial, SphereGeometry, TorusGeometry, TubeGeometry, Vector3, AdditiveBlending, DoubleSide, MeshBasicMaterial, MeshStandardMaterial,
  type Texture,
} from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { EV, HazardKind } from '@shared/events';
import { RoundPhase } from '@shared/ids';
import { TUNING } from '@shared/maps/spincycle';
import { hullGeometry, mergeKeep, prep, seeded, sweep, type ProfilePoint } from '../kit/geometry';
import { GLSL_NOISE } from '../kit/glsl';
import { outlineMaterial, surfaceMaterial } from '../kit/materials';
import { B, type Arena, type ArenaFrame, type ArenaKit, type ArenaLayout, type DebugHazard } from './types';

const R = TUNING.floorRadius;
const HUB = TUNING.hub;
/** §6.5 sweeper arms: capsules from r 0.9 to 7.6, radius 0.25, top 0.55. */
export const ARM = { r0: 0.9, r1: 7.6, radius: 0.25, top: 0.55 } as const;
const ENAMEL = '#F4FBFF';
const AQUA = '#4FD1C5';
const STEEL = '#9AA9B8';
const WARNING = '#FF4D4D';
const SUDS = '#CFEFFF';
const BRISTLES_PER_ARM = 60;
const MAX_ARMS = 3;
const BUBBLES = 6;
const BEATS_PER_BAR = 4;

const glslColor = (c: string): string => `vec3(${new Color(c).toArray().map((v) => v.toFixed(4)).join(', ')})`;

const FLOOR_PARS = /* glsl */ `
uniform vec4 uArms;     // three arm angles (sim radians) + count
uniform float uArmDir;
uniform float uArmSpeed;
uniform vec2 uShadowArm; // telegraph: angle, alpha
`;

const FLOOR_ALBEDO = /* glsl */ `
  vec2 p = envObj.xz;
  float r = length(p);
  float ang = atan(p.y, p.x);
  float aa = fwidth(r) * 1.3 + 0.002;
  vec3 steel = ${glslColor('#DCE5EE')};
  vec3 col = steel * (0.94 + 0.06 * envNoise(p * 0.8));
  if (envNo.y > 0.5) {
    // Brushed steel: fine concentric grain.
    col *= 0.97 + 0.03 * sin(r * 160.0 + envNoise(vec2(ang * 40.0, r)) * 4.0);
    // Perforation: a hex grid of dark holes between the pressed rings.
    float holeD = envHexDist(p, 0.36);
    float holes = (1.0 - smoothstep(0.055, 0.055 + fwidth(holeD) * 1.5 + 0.004, holeD)) * step(1.3, r) * step(r, 6.55);
    holes *= step(0.12, abs(r - 2.9)) * step(0.12, abs(r - 4.8));
    col = mix(col, ${glslColor('#3A4656')}, holes);
    // Pressed rings.
    for (int k = 0; k < 2; k++) {
      float rr = k == 0 ? 2.9 : 4.8;
      float d = r - rr;
      col *= 1.0 + 0.12 * smoothstep(0.08, 0.0, abs(d - 0.04)) - 0.12 * smoothstep(0.08, 0.0, abs(d + 0.04));
    }
    // Aqua inner ring around the hub, and the hazard-striped rim band.
    col = mix(col, ${glslColor(AQUA)}, smoothstep(${(HUB.radius + 0.05).toFixed(2)}, ${(HUB.radius + 0.08).toFixed(2)}, r) * (1.0 - smoothstep(1.18, 1.2, r)));
    float edge = smoothstep(6.62 - aa, 6.62 + aa, r);
    float stripes = step(0.5, fract(ang * 36.0 / 6.2831853 + r * 0.6));
    col = mix(col, mix(${glslColor(WARNING)}, ${glslColor(ENAMEL)}, stripes), edge);
    // Wet soap streaks trailing each arm.
    float wet = 0.0;
    for (int i = 0; i < 3; i++) {
      if (float(i) >= uArms.w) break;
      float a = i == 0 ? uArms.x : (i == 1 ? uArms.y : uArms.z);
      float behind = mod((a - ang) * uArmDir, 6.2831853);
      wet = max(wet, exp(-behind * 2.2) * smoothstep(0.0, 0.05, behind) * min(1.0, uArmSpeed * 0.9));
    }
    wet *= smoothstep(1.0, 1.5, r) * (1.0 - edge);
    float suds = smoothstep(0.55, 0.8, envNoise(p * 4.0 + vec2(uTime * 0.3, 0.0)));
    col = mix(col, ${glslColor(SUDS)} * 1.05, wet * (0.35 + 0.55 * suds));
    // Third-arm telegraph: its shadow on the floor.
    if (uShadowArm.y > 0.0) {
      float da = abs(mod(ang - uShadowArm.x + 3.14159265, 6.2831853) - 3.14159265);
      float lateral = da * r;
      float sh = (1.0 - smoothstep(0.18, 0.42, lateral)) * step(${ARM.r0.toFixed(2)}, r);
      col *= 1.0 - 0.55 * sh * uShadowArm.y;
    }
  } else {
    col = ${glslColor(STEEL)} * 0.9;
  }
  albedo = col;
`;

const BUBBLE_VERTEX = /* glsl */ `
attribute vec2 aBubble;   // x = wobble, y = seed
varying vec3 vN;
varying vec3 vV;
varying float vSeed;
varying float vWob;
uniform float uTime;
void main() {
  vec3 p = position;
  float w = aBubble.x;
  float s = aBubble.y;
  // Wobble before the pop: jiggling lobes that grow over the 2-beat telegraph.
  float j = w * (0.08 * sin(uTime * 24.0 + p.y * 6.0 + s * 9.0) + 0.06 * sin(uTime * 31.0 + p.x * 7.0));
  p *= 1.0 + j;
  p.y *= 1.0 + 0.04 * sin(uTime * 3.0 + s * 6.0);
  vec4 mv = modelViewMatrix * instanceMatrix * vec4(p, 1.0);
  vN = normalize(normalMatrix * mat3(instanceMatrix) * normal);
  vV = normalize(-mv.xyz);
  vSeed = s;
  vWob = w;
  gl_Position = projectionMatrix * mv;
}`;

const BUBBLE_FRAGMENT = /* glsl */ `
uniform float uTime;
varying vec3 vN;
varying vec3 vV;
varying float vSeed;
varying float vWob;
void main() {
  float f = 1.0 - abs(dot(normalize(vN), normalize(vV)));
  float fr = pow(f, 2.2);
  vec3 irid = 0.5 + 0.5 * cos(6.2831 * (f * 1.6 + vSeed + uTime * 0.15 + vec3(0.0, 0.33, 0.67)));
  vec3 col = mix(vec3(0.85, 0.97, 1.0), irid, 0.55) * (0.25 + 1.1 * fr);
  // A window-shaped highlight.
  vec3 n = normalize(vN);
  float hl = smoothstep(0.86, 0.95, dot(n, normalize(vec3(-0.45, 0.6, 0.65))));
  col += hl * 1.4;
  float alpha = clamp(0.1 + fr * 0.75 + hl, 0.0, 1.0);
  alpha *= 1.0 + vWob * 0.3 * sin(uTime * 30.0);
  gl_FragColor = vec4(col * alpha, alpha);
}`;

interface BubbleAnim { x: number; y: number; scale: number; wobble: number; seen: boolean; popAt: number }

export interface SpinDebug {
  arms: InstancedMesh;
  armCount(): number;
  /** The rendered angle (sim radians) of arm i. */
  armAngle(i: number): number;
  domes(): number;
}

export function buildSpin(kit: ArenaKit): Arena & { spin: SpinDebug } {
  const root = new Group();
  root.name = 'arena:spin';
  const owned: { dispose(): void }[] = [];
  const p = kit.palette;
  const env = kit.env;

  // ---- Drum floor -------------------------------------------------------------------------------
  const fu = {
    uArms: { value: { x: 0, y: 0, z: 0, w: 0 } }, uArmDir: { value: 1 }, uArmSpeed: { value: 0 }, uShadowArm: { value: { x: 0, y: 0 } },
  };
  const floorMat = surfaceMaterial(env, kit.ramp, { key: 'spin-floor', color: '#FFFFFF', rim: 0.3, gloss: 0.55, glossEdge: 0.96, pars: FLOOR_PARS, albedo: FLOOR_ALBEDO, uniforms: fu as never });
  const seg = kit.seg(96, 40);
  const floorGeo = sweep([
    [[0, -0.3], [R - 0.05, -0.3], [R, -0.25]],
    [[R, -0.25], [R, -0.02], [R - 0.03, 0]],
    [[R - 0.03, 0], [0, 0]],
  ], seg);
  const floor = new Mesh(floorGeo, floorMat);
  floor.name = 'drum-floor';
  floor.receiveShadow = true;
  root.add(floor);
  owned.push(floorGeo, floorMat);

  // Rim, drum shell, warning domes (static).
  const bg = kit.batch;
  const rim = new TorusGeometry(R + 0.18, 0.22, kit.seg(12, 8), seg);
  rim.rotateX(Math.PI / 2);
  bg.add(B.chrome, rim, new Matrix4().makeTranslation(0, -0.08, 0), { color: '#E4EAF1' });
  bg.add(B.hullBold, new TorusGeometry(R + 0.18, 0.22, 8, 48).rotateX(Math.PI / 2), new Matrix4().makeTranslation(0, -0.08, 0));
  // The drum back (§12.5 underside): a perforated pressed-steel tub hovering over the suds, a
  // three-armed drum spider on its back and the drive shaft dipping into the foam.
  const shellRuns: ProfilePoint[][] = [[[R + 0.24, -0.26], [R + 0.24, -2.0], [R + 0.12, -2.3], [R - 0.3, -2.48], [1.6, -2.7], [0, -2.76]]];
  const shellGeo = sweep(shellRuns, seg);
  const drumMat = drumMaterial(kit.tier === 'potato' ? null : kit.envMap, R + 0.24);
  const shell = new Mesh(shellGeo, drumMat);
  shell.name = 'drum-back';
  root.add(shell);
  owned.push(shellGeo, drumMat);
  if (kit.rich) bg.add(B.hullBold, sweep(shellRuns, 48), null);
  for (let k = 0; k < 3; k++) {
    const a = (k / 3) * Math.PI * 2 + 0.5;
    const arm = new RoundedBoxGeometry(R - 1.2, 0.34, 0.62, 2, 0.12);
    const am = new Matrix4().makeRotationY(-a).multiply(new Matrix4().makeRotationZ(0.035)).setPosition(Math.cos(a) * (R / 2 + 0.05), -2.82 + 0.035 * (R / 2), Math.sin(a) * (R / 2 + 0.05));
    bg.add(B.toon, arm, am, { color: '#7E8D9C' });
    if (kit.rich) bg.add(B.hull, new RoundedBoxGeometry(R - 1.2, 0.34, 0.62, 1, 0.12), am);
  }
  bg.add(B.chrome, new CylinderGeometry(1.0, 1.15, 0.5, kit.seg(24, 10)), new Matrix4().makeTranslation(0, -2.95, 0), { color: '#C9D3DE' });
  bg.add(B.chrome, new CylinderGeometry(0.42, 0.42, 1.4, kit.seg(16, 8)), new Matrix4().makeTranslation(0, -3.7, 0), { color: '#E4EAF1' });

  // Warning domes on the rim (they flash before a reversal).
  const domeUniform = { value: 0 };
  const domeGeo = sweep([[[0.42, 0], [0.4, 0.12], [0.32, 0.3], [0.16, 0.4], [0, 0.42]]], kit.seg(16, 8));
  const domeMat = surfaceMaterial(env, kit.ramp, {
    key: 'spin-dome', color: WARNING, rim: 0.4, gloss: 0.9, pars: 'uniform float uAlarm;\n', uniforms: { uAlarm: domeUniform },
    emissive: 'glow += vec3(1.0, 0.18, 0.12) * (0.08 + uAlarm * 2.4);',
  });
  const domes = new InstancedMesh(domeGeo, domeMat, 4);
  for (let k = 0; k < 4; k++) {
    const a = Math.PI / 4 + (k * Math.PI) / 2;
    domes.setMatrixAt(k, new Matrix4().makeTranslation(Math.cos(a) * (R + 0.18), 0.12, Math.sin(a) * (R + 0.18)));
  }
  domes.name = 'warning-domes';
  root.add(domes);
  owned.push(domeGeo, domeMat);
  const domeBase = new CylinderGeometry(0.48, 0.5, 0.14, kit.seg(16, 8));
  for (let k = 0; k < 4; k++) {
    const a = Math.PI / 4 + (k * Math.PI) / 2;
    bg.add(B.gloss, domeBase.clone(), new Matrix4().makeTranslation(Math.cos(a) * (R + 0.18), 0.06, Math.sin(a) * (R + 0.18)), { color: '#5B6878' });
  }
  domeBase.dispose();

  // ---- Hub (agitator) -------------------------------------------------------------------------
  const hubGroup = new Group();
  hubGroup.name = 'hub';
  root.add(hubGroup);
  const hb = new Group();
  hubGroup.add(hb);
  const hubGeo = sweep([[[HUB.radius, 0], [HUB.radius, 0.12], [0.8, 0.3], [0.62, 0.72], [0.52, HUB.top - 0.06], [0.42, HUB.top], [0, HUB.top + 0.05]]], kit.seg(28, 12));
  const hubParts = [prep(hubGeo, null, { color: AQUA, ao: [0, 0.4, 0.8] })];
  for (let k = 0; k < 6; k++) {
    const fin = new RoundedBoxGeometry(0.16, 0.7, 0.62, 1, 0.06);
    const a = (k / 6) * Math.PI * 2;
    hubParts.push(prep(fin, new Matrix4().makeRotationY(-a).multiply(new Matrix4().makeRotationZ(0.22)).setPosition(Math.cos(a) * 0.62, 0.42, Math.sin(a) * 0.62), { color: '#E9FBF8' }));
  }
  hubParts.push(prep(new SphereGeometry(0.28, 12, 8), new Matrix4().makeTranslation(0, HUB.top + 0.04, 0), { color: WARNING }));
  const hubAll = mergeKeep(hubParts, ['position', 'normal', 'color']);
  const hubMesh = new Mesh(hubAll, kit.mats.gloss);
  hubMesh.castShadow = true;
  hubMesh.receiveShadow = true;
  hb.add(hubMesh);
  const hubHull = new Mesh(hullGeometry(sweep([[[HUB.radius + 0.02, 0], [HUB.radius, 0.14], [0.8, 0.32], [0.62, 0.74], [0.42, HUB.top], [0, HUB.top + 0.06]]], 20)), kit.mats.hull);
  hb.add(hubHull);
  owned.push(hubAll, hubHull.geometry);

  // ---- Sweeper arms (instanced core + bristles) ------------------------------------------------
  const armLen = ARM.r1 - ARM.r0;
  const coreGeo = prep(new RoundedBoxGeometry(armLen + ARM.radius * 2, ARM.top - 0.02, ARM.radius * 1.8, 3, 0.12), new Matrix4().makeTranslation((ARM.r0 + ARM.r1) / 2, (ARM.top - 0.02) / 2 + 0.01, 0), { color: AQUA, uv: false });
  // A white racing stripe on top and a red tip cap.
  const stripe = prep(new RoundedBoxGeometry(armLen - 0.4, 0.04, 0.16, 1, 0.02), new Matrix4().makeTranslation((ARM.r0 + ARM.r1) / 2, ARM.top, 0), { color: '#FFFFFF' });
  const tip = prep(new SphereGeometry(ARM.radius * 1.05, 12, 8), new Matrix4().makeScale(0.9, 1.0, 1.0).setPosition(ARM.r1, ARM.top * 0.55, 0), { color: WARNING });
  const armGeo = mergeKeep([coreGeo, stripe, tip], ['position', 'normal', 'color']);
  const arms = new InstancedMesh(armGeo, kit.mats.gloss, MAX_ARMS);
  arms.name = 'sweeper-arms';
  arms.castShadow = true;
  arms.receiveShadow = true;
  arms.instanceMatrix.setUsage(DynamicDrawUsage);
  arms.frustumCulled = false;
  arms.userData.hazard = true;
  root.add(arms);
  const armHullSrc = new RoundedBoxGeometry(armLen + ARM.radius * 2, ARM.top - 0.02, ARM.radius * 1.8, 1, 0.12).translate((ARM.r0 + ARM.r1) / 2, (ARM.top - 0.02) / 2 + 0.01, 0);
  const armHullGeo = hullGeometry(armHullSrc);
  armHullSrc.dispose();
  const armHull = new InstancedMesh(armHullGeo, outlineMaterial(0.045, kit.outlineScale), MAX_ARMS);
  armHull.instanceMatrix = arms.instanceMatrix;
  armHull.frustumCulled = false;
  root.add(armHull);
  owned.push(armGeo, armHullGeo, armHull.material as ShaderMaterial);
  const bristleCount = kit.tier === 'potato' ? 24 : kit.tier === 'low' ? 36 : BRISTLES_PER_ARM;
  const brGeo = prep(new CylinderGeometry(0.026, 0.04, 0.34, 4).translate(0, -0.17, 0), null, { color: '#FFFFFF', ao: [-0.34, 0, 0.88] });
  const bristles = new InstancedMesh(brGeo, kit.mats.toon, MAX_ARMS * bristleCount);
  bristles.name = 'bristles';
  bristles.instanceMatrix.setUsage(DynamicDrawUsage);
  bristles.frustumCulled = false;
  bristles.userData.hazardDecor = true;
  const brCol = new Color();
  for (let i = 0; i < MAX_ARMS * bristleCount; i++) bristles.setColorAt(i, brCol.set(i % 3 === 0 ? '#CFF6F1' : '#F4FBFF'));
  root.add(bristles);
  owned.push(brGeo);

  // ---- Bubbles ----------------------------------------------------------------------------------
  const bubGeo = new SphereGeometry(0.6, kit.seg(28, 14), kit.seg(20, 10));
  const bubData = new Float32Array(BUBBLES * 2);
  const bubAttr = new InstancedBufferAttribute(bubData, 2).setUsage(DynamicDrawUsage);
  bubGeo.setAttribute('aBubble', bubAttr);
  const bubMat = new ShaderMaterial({
    uniforms: { uTime: env.uTime }, vertexShader: BUBBLE_VERTEX, fragmentShader: BUBBLE_FRAGMENT,
    transparent: true, depthWrite: false, premultipliedAlpha: true, toneMapped: false,
  });
  const bubbles = new InstancedMesh(bubGeo, bubMat, BUBBLES);
  bubbles.name = 'soap-bubbles';
  bubbles.instanceMatrix.setUsage(DynamicDrawUsage);
  bubbles.frustumCulled = false;
  bubbles.renderOrder = 6;
  bubbles.userData.hazardDecor = true;
  root.add(bubbles);
  owned.push(bubGeo, bubMat);
  const bubShadowMat = new MeshBasicMaterial({ map: kit.lib.blobShadowTexture, transparent: true, depthWrite: false, opacity: 0.22, color: '#000000', toneMapped: false });
  const bubShadowGeo = new PlaneGeometry(1.4, 1.4).rotateX(-Math.PI / 2);
  const bubShadows = new InstancedMesh(bubShadowGeo, bubShadowMat, BUBBLES);
  bubShadows.instanceMatrix.setUsage(DynamicDrawUsage);
  bubShadows.frustumCulled = false;
  bubShadows.renderOrder = 1;
  root.add(bubShadows);
  owned.push(bubShadowMat, bubShadowGeo);
  for (let i = 0; i < BUBBLES; i++) bubData[i * 2 + 1] = i * 0.173;
  // Pop rings (additive, one per bubble slot).
  const popGeo = new PlaneGeometry(2, 2).rotateX(-Math.PI / 2);
  const popData = new Float32Array(BUBBLES);
  const popAttr = new InstancedBufferAttribute(popData, 1).setUsage(DynamicDrawUsage);
  popGeo.setAttribute('aAge', popAttr);
  const popMat = new ShaderMaterial({
    vertexShader: /* glsl */ `
      attribute float aAge;
      varying vec2 vUv;
      varying float vAge;
      void main() {
        vUv = uv;
        vAge = aAge;
        float s = 0.4 + 1.4 * (1.0 - pow(1.0 - clamp(aAge, 0.0, 1.0), 3.0));
        gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position * s, 1.0);
        // Idle slots never rasterize (they only discarded in the fragment shader).
        if (aAge <= 0.0 || aAge >= 1.0) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      varying vec2 vUv;
      varying float vAge;
      void main() {
        if (vAge <= 0.0 || vAge >= 1.0) discard;
        float r = length(vUv * 2.0 - 1.0);
        float ring = 1.0 - smoothstep(0.0, 0.08, abs(r - 0.85));
        float a = ring * (1.0 - vAge);
        gl_FragColor = vec4(vec3(0.8, 0.97, 1.0) * a, a);
      }`,
    transparent: true, depthWrite: false, blending: AdditiveBlending, side: DoubleSide, toneMapped: false,
  });
  const pops = new InstancedMesh(popGeo, popMat, BUBBLES);
  pops.instanceMatrix.setUsage(DynamicDrawUsage);
  pops.frustumCulled = false;
  pops.renderOrder = 5;
  root.add(pops);
  owned.push(popGeo, popMat);
  const bub: BubbleAnim[] = Array.from({ length: BUBBLES }, () => ({ x: 0, y: 0, scale: 0, wobble: 0, seen: false, popAt: -10 }));

  // ---- Background: porthole door, socks, a rubber duck -----------------------------------------
  laundryProps(kit);

  // ---- Per-frame -------------------------------------------------------------------------------
  const m = new Matrix4();
  const m2 = new Matrix4();
  const q = new Quaternion();
  const UP = new Vector3(0, 1, 0);
  const X = new Vector3(1, 0, 0);
  const pos = new Vector3();
  const scl = new Vector3();
  const armAngles = [0, 0, 0];
  let armCount = 0;
  let lastAngle = 0;
  let speed = 0;
  let thirdDropAt = -1;
  let prevDrop = 0;
  let landedAt = -10;
  let prevCount = 0;
  let alarm = 0;
  let alarmEv = -1;
  let thirdWarnEv = -1;
  const brSide = new Float32Array(bristleCount);
  const brR = new Float32Array(bristleCount);
  const brJit = new Float32Array(bristleCount);
  const rnd = seeded(kit.seed + 41);
  for (let k = 0; k < bristleCount; k++) {
    brSide[k] = k % 2 === 0 ? 1 : -1;
    brR[k] = ARM.r0 + 0.25 + ((Math.floor(k / 2) + 0.5) / Math.ceil(bristleCount / 2)) * (armLen - 0.4);
    brJit[k] = rnd();
  }

  const layout: ArenaLayout = {
    sky: { top: p.skyTop, bottom: p.skyBottom, cloud: '#FFFFFF', clouds: 0.7, stars: 0, sunGlow: 0.85 },
    voidColors: { a: '#FFFFFF', b: SUDS, c: p.skyTop },
    voidEdge: R + 0.6,
    voidShadow: { x: 0, y: 0, hx: 0, hy: 0, r: R + 0.6, strength: 0.6 },
    splash: { ring: '#FFFFFF', edge: SUDS },
    audience: { cx: 0, cy: 0, rx: R + 6.5, ry: R + 6, y: -0.8, plank: '#E9F3FA', trim: AQUA, fur: ['#4FD1C5', '#FFFFFF', '#8FB8FF', '#FFB3C7', '#FFC93C', '#CFEFFF', '#B9A4FF'] },
    confetti: { colors: [AQUA, '#8FB8FF', '#FFFFFF', WARNING, '#FFC93C'], r0: 18, r1: 40, y0: -2, y1: 18 },
    shadowY: [-0.4, 3.0],
  };

  function popBubble(i: number, x: number, y: number, now: number): void {
    const b = bub[i];
    if (now - b.popAt < 0.3) return;
    b.popAt = now;
    kit.particles.burst({ x, y: 0.75, z: y, count: 12, color: ['#FFFFFF', SUDS, '#BFF1EC'], speed: 3.5, up: 2.8, size: 0.07, life: 0.7, spread: 0.3, gravity: 9 });
    m.makeTranslation(x, 0.05, y);
    pops.setMatrixAt(i, m);
    pops.instanceMatrix.needsUpdate = true;
  }

  return {
    root, layout,
    spin: {
      arms,
      armCount: () => armCount,
      armAngle: (i) => armAngles[i],
      domes: () => alarm,
    },
    update(f: ArenaFrame) {
      const v = f.view;
      const hz = v.hazard;
      const c = v.clock;
      armCount = Math.min(MAX_ARMS, hz.armCount);
      for (let i = 0; i < MAX_ARMS; i++) armAngles[i] = hz.armAngles[i] ?? 0;
      // Angular speed (smoothed) for bristle bend, streaks and the hub: the pose's armOmega when the
      // map provides it, else measured from the frame-to-frame angle.
      if (hz.armOmega !== undefined) {
        speed += (Math.abs(hz.armOmega) - speed) * Math.min(1, f.dt * 8);
        lastAngle = armAngles[0];
      } else if (armCount > 0) {
        let d = armAngles[0] - lastAngle;
        d = Math.atan2(Math.sin(d), Math.cos(d));
        const w = f.dt > 1e-4 ? Math.abs(d) / f.dt : speed;
        if (w < 20) speed += (w - speed) * Math.min(1, f.dt * 6);
        lastAngle = armAngles[0];
      } else speed *= 0.9;
      const dir = hz.armDir >= 0 ? 1 : -1;
      // The Sudden Death third arm: WP2's armDrop runs 0 → 1 over its 1-bar shadow, landing at 1.
      const drop3 = hz.armDrop;
      if (drop3 !== undefined) {
        if (armCount === 3 && drop3 >= 1 && prevDrop < 1 && prevDrop > 0) {
          landedAt = f.time;
          const a = armAngles[2];
          for (const rr of [2.5, 4.5, 6.5]) kit.particles.burst({ x: Math.cos(a) * rr, y: 0.1, z: Math.sin(a) * rr, count: 5, color: ['#FFFFFF', SUDS], speed: 2.4, up: 2.6, size: 0.08, life: 0.7, spread: 0.6, gravity: 10 });
        }
        prevDrop = armCount === 3 ? drop3 : 0;
      } else if (armCount === 3 && prevCount < 3) thirdDropAt = f.time;
      prevCount = armCount;
      // Arms (and the hub turning with them).
      for (let i = 0; i < MAX_ARMS; i++) {
        if (i >= armCount) {
          arms.setMatrixAt(i, m.makeScale(0, 0, 0));
          continue;
        }
        let drop = 0;
        if (i === 2 && drop3 !== undefined) {
          // Hangs high over its shadow, then falls in over the last part of the bar and bounces.
          const u = Math.min(1, drop3);
          drop = u < 1 ? 9 * Math.pow(1 - u, 3) : 0;
          const t = f.time - landedAt;
          if (u >= 1 && t < 1) drop = Math.max(0, 0.16 * Math.sin(t * 18) * Math.exp(-t * 6));
        } else if (i === 2 && thirdDropAt >= 0) {
          const t = f.time - thirdDropAt;
          drop = t < 0.4 ? 5 * (1 - t / 0.4) * (1 - t / 0.4) : Math.max(0, 0.12 * Math.sin((t - 0.4) * 18) * Math.exp(-(t - 0.4) * 6));
        }
        q.setFromAxisAngle(UP, -armAngles[i]);
        arms.setMatrixAt(i, m.compose(pos.set(0, drop, 0), q, scl.set(1, 1, 1)));
      }
      arms.instanceMatrix.needsUpdate = true;
      hubGroup.rotation.y = armCount > 0 ? -armAngles[0] : hubGroup.rotation.y;
      // Bristles bend toward the trailing side with speed (§11.4).
      const bendBase = Math.min(0.75, speed * 0.32);
      let bi = 0;
      for (let i = 0; i < MAX_ARMS; i++) {
        for (let k = 0; k < bristleCount; k++, bi++) {
          if (i >= armCount) {
            bristles.setMatrixAt(bi, m.makeScale(0, 0, 0));
            continue;
          }
          const r = brR[k];
          const bend = bendBase * (0.4 + 0.6 * (r / ARM.r1)) * dir;
          q.setFromAxisAngle(UP, -armAngles[i]);
          m.compose(pos.set(0, 0, 0), q, scl.set(1, 1, 1));
          // Local frame: +x along the arm, +z = the arm's sim +angle side (leading for dir > 0).
          m2.makeTranslation(r, 0.34, brSide[k] * 0.2 + (brJit[k] - 0.5) * 0.05);
          m.multiply(m2);
          m.multiply(m2.makeRotationAxis(X, -bend + brSide[k] * 0.22 + Math.sin(f.time * 9 + brJit[k] * 20) * 0.03 * speed));
          bristles.setMatrixAt(bi, m);
        }
      }
      bristles.instanceMatrix.needsUpdate = true;
      fu.uArms.value = { x: armAngles[0], y: armAngles[1], z: armAngles[2], w: armCount };
      fu.uArmDir.value = dir;
      fu.uArmSpeed.value = speed;
      // Third arm telegraph: its shadow for the bar before Sudden Death (or on HAZARD_WARN).
      const barTicks = c.tpb * BEATS_PER_BAR;
      const pre = Number.isFinite(c.sdStart) && c.tick >= c.sdStart - barTicks && c.tick < c.sdStart && armCount < 3;
      const evWarn = thirdWarnEv >= 0 && f.time - thirdWarnEv < 2.5 && armCount < 3;
      let k = pre ? Math.min(1, (c.tick - (c.sdStart - barTicks)) / barTicks) : evWarn ? Math.min(1, (f.time - thirdWarnEv) / 1.5) : 0;
      if (drop3 !== undefined) k = armCount === 3 && drop3 < 1 ? Math.min(1, 0.35 + drop3) : 0;
      const shadowAngle = armCount === 3 ? armAngles[2] : armAngles[0] + Math.PI / 2;
      fu.uShadowArm.value = { x: shadowAngle, y: k * (0.6 + 0.4 * Math.sin(f.time * 10)) };
      // Reversal alarm: red domes flash on the 2 beats before each Sudden Death reversal.
      let warn = false;
      if (hz.reverseWarn !== undefined) warn = hz.reverseWarn > 0;
      else if (c.phase === RoundPhase.SUDDEN_DEATH) {
        const beatsIn = (c.tick - c.sdStart) / c.tpb;
        const period = 4 * BEATS_PER_BAR;
        const toRev = period - (beatsIn - Math.floor(beatsIn / period) * period);
        warn = toRev <= 2;
      }
      if (alarmEv >= 0 && f.time - alarmEv < 1.2) warn = true;
      alarm = warn ? (Math.sin(f.time * Math.PI * 8) > 0 ? 1 : 0.25) : Math.max(0, alarm - f.dt * 4);
      domeUniform.value = alarm;
      // Bubbles from the pose; a pop is a bubble that vanished or jumped after its wobble.
      for (let i = 0; i < BUBBLES; i++) {
        const src = hz.bubbles[i];
        const b = bub[i];
        const scale = src ? src.scale : 0;
        if (b.seen && b.scale > 0.3 && (scale < b.scale * 0.5 || Math.hypot(src.x - b.x, src.y - b.y) > 1.5) && b.wobble > 0.3) {
          popBubble(i, b.x, b.y, f.time);
        }
        b.seen = true;
        b.x = src?.x ?? 0;
        b.y = src?.y ?? 0;
        b.scale = scale;
        b.wobble = src?.wobble ?? 0;
        const bob = 0.75 + 0.08 * Math.sin(f.time * 1.7 + i);
        m.compose(pos.set(b.x, bob * Math.max(0.4, scale), b.y), q.identity(), scl.setScalar(Math.max(0.0001, scale)));
        bubbles.setMatrixAt(i, m);
        bubData[i * 2] = b.wobble;
        m.compose(pos.set(b.x, 0.015, b.y), q.identity(), scl.setScalar(Math.max(0.0001, scale)));
        bubShadows.setMatrixAt(i, m);
        popData[i] = Math.min(1, (f.time - bub[i].popAt) / 0.45);
      }
      bubbles.instanceMatrix.needsUpdate = true;
      bubShadows.instanceMatrix.needsUpdate = true;
      bubAttr.needsUpdate = true;
      popAttr.needsUpdate = true;
    },
    onEvent(ev, f) {
      if (ev.type === EV.HAZARD_WARN && ev.b === HazardKind.SWEEPER_REVERSE) alarmEv = f.time;
      else if (ev.type === EV.HAZARD_WARN && ev.b === HazardKind.THIRD_ARM) thirdWarnEv = f.time;
      else if (ev.type === EV.HAZARD_FIRE && ev.b === HazardKind.BUBBLE && ev.c >= 0 && ev.c < BUBBLES) popBubble(ev.c, ev.x, ev.y, f.time);
    },
    debugHazards() {
      const out: DebugHazard[] = [];
      for (let i = 0; i < armCount; i++) {
        const a = armAngles[i];
        const y = ARM.top / 2;
        out.push({ kind: 'arm', index: i, a: new Vector3(Math.cos(a) * ARM.r0, y, Math.sin(a) * ARM.r0), b: new Vector3(Math.cos(a) * ARM.r1, y, Math.sin(a) * ARM.r1), radius: ARM.radius });
      }
      for (let i = 0; i < BUBBLES; i++) {
        if (bub[i].scale > 0.05) {
          const c = new Vector3(bub[i].x, 0.75, bub[i].y);
          out.push({ kind: 'bubble', index: i, a: c, b: c.clone(), radius: 0.6 * bub[i].scale });
        }
      }
      return out;
    },
    dispose() {
      for (const o of owned) o.dispose();
    },
  };
}

/** The giant porthole door behind the drum, floating socks and a rubber duck in the suds. */
function laundryProps(kit: ArenaKit): void {
  const bg = kit.batch;
  const seg = (k: number): number => kit.seg(k, 8);
  // Porthole door ring standing behind the arena, glass in it.
  const door = new Matrix4().makeRotationX(-0.35).setPosition(0, 1.5, -17.5);
  bg.add(B.bg, new TorusGeometry(7.2, 1.0, seg(14), seg(56)), door, { color: '#E9F1F8' });
  bg.add(B.chrome, new TorusGeometry(6.1, 0.35, seg(10), seg(48)), door, { color: '#E4EAF1' });
  bg.add(B.bgGlow, new CylinderGeometry(5.9, 5.9, 0.08, seg(48)).rotateX(Math.PI / 2), door.clone().multiply(new Matrix4().makeTranslation(0, 0, -0.2)), { color: '#BFE7F7' });
  const handle = new RoundedBoxGeometry(1.0, 3.2, 0.8, 2, 0.3);
  bg.add(B.chrome, handle, door.clone().multiply(new Matrix4().makeTranslation(7.9, 0, 0.4)), { color: '#E4EAF1' });
  if (kit.rich) bg.add(B.bgHull, new TorusGeometry(7.2, 1.0, 10, 40), door);
  // Socks: striped tubes drifting in the suds.
  const sockColors = [['#FF8FB1', '#FFFFFF'], ['#4FD1C5', '#FFC93C'], ['#8FB8FF', '#FFFFFF']];
  const spots: [number, number, number][] = [[-12.5, -4, 0.4], [12.5, 2, -0.8], [-9.5, -12.5, 2.1]];
  spots.forEach(([x, z, yaw], i) => {
    const pts = [new Vector3(0, 0, 0), new Vector3(1.6, 0.1, 0.2), new Vector3(3.0, 0.15, -0.2), new Vector3(3.6, 0.1, -1.2), new Vector3(4.3, 0.05, -1.6)];
    const curve = new CatmullRomCurve3(pts);
    const sock = new TubeGeometry(curve, 20, 0.55, seg(10), false);
    const mm = new Matrix4().makeRotationY(yaw).setPosition(x, -2.85, z);
    const [c0, c1] = sockColors[i % sockColors.length];
    // Stripes: split the tube into colored rings by vertex position along the curve.
    const g = sock.toNonIndexed();
    sock.dispose();
    const posA = g.getAttribute('position');
    const colArr = new Float32Array(posA.count * 3);
    const ca = new Color(c0);
    const cb = new Color(c1);
    for (let k = 0; k < posA.count; k++) {
      const t = posA.getX(k) / 4.3;
      const cc = Math.floor(t * 7) % 2 === 0 ? ca : cb;
      colArr.set([cc.r, cc.g, cc.b], k * 3);
    }
    g.deleteAttribute('uv');
    g.setAttribute('color', new Float32BufferAttribute(colArr, 3));
    bg.add(B.bg, g, mm, { keepColor: true, ao: [-3.4, -2.5, 0.8] });
  });
  // A rubber duck bobbing at the front left.
  const duck = new Matrix4().makeRotationY(0.9).setPosition(-10.5, -2.75, 6.5);
  bg.add(B.bg, new SphereGeometry(1.2, seg(18), seg(12)), duck.clone().multiply(new Matrix4().makeScale(1.25, 0.85, 1)), { color: '#FFD93C', ao: [-3, -2.2, 0.75] });
  bg.add(B.bg, new SphereGeometry(0.75, seg(16), seg(12)), duck.clone().multiply(new Matrix4().makeTranslation(0.8, 1.05, 0)), { color: '#FFD93C' });
  bg.add(B.bg, new SphereGeometry(0.35, seg(10), seg(8)), duck.clone().multiply(new Matrix4().makeTranslation(1.55, 0.95, 0)).multiply(new Matrix4().makeScale(1.2, 0.5, 0.9)), { color: '#FF8A3C' });
  for (const s of [-1, 1]) bg.add(B.bg, new SphereGeometry(0.1, 8, 6), duck.clone().multiply(new Matrix4().makeTranslation(1.25, 1.25, s * 0.38)), { color: '#2A1A3A' });
  if (kit.rich) bg.add(B.bgHull, new SphereGeometry(1.2, 12, 8), duck.clone().multiply(new Matrix4().makeScale(1.25, 0.85, 1)));
}

/**
 * The drum tub: polished steel mirroring the map's environment, with a band of perforations
 * (hex-packed holes) and two pressed ridges, painted in object space.
 */
function drumMaterial(env: Texture | null, radius: number): MeshStandardMaterial {
  // Without an environment (Potato: no PMREM bake) it is satin steel lit by the scene's lights.
  const m = env
    ? new MeshStandardMaterial({ color: '#D8E1EA', metalness: 0.9, roughness: 0.3, envMap: env, envMapIntensity: 1.0 })
    : new MeshStandardMaterial({ color: '#C9D3DE', metalness: 0.35, roughness: 0.45 });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vDrumObj;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvDrumObj = position;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>\nvarying vec3 vDrumObj;\n${GLSL_NOISE}`)
      .replace('#include <color_fragment>', `#include <color_fragment>
  float drumY = vDrumObj.y;
  float drumA = atan(vDrumObj.z, vDrumObj.x);
  float drumHex = envHexDist(vec2(drumA * ${radius.toFixed(3)}, drumY), 0.4);
  float drumHole = (1.0 - smoothstep(0.07, 0.07 + fwidth(drumHex) * 1.5 + 0.004, drumHex)) * step(-1.8, drumY) * step(drumY, -0.62) * step(${(radius - 0.05).toFixed(3)}, length(vDrumObj.xz));
  float drumRidge = smoothstep(0.06, 0.0, abs(drumY + 0.45)) + smoothstep(0.06, 0.0, abs(drumY + 1.95));
  diffuseColor.rgb *= (1.0 - 0.82 * drumHole) * (1.0 + 0.18 * drumRidge);`)
      .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\nroughnessFactor = mix(roughnessFactor, 0.85, drumHole);')
      .replace('#include <metalnessmap_fragment>', '#include <metalnessmap_fragment>\nmetalnessFactor *= 1.0 - 0.85 * drumHole;');
  };
  m.customProgramCacheKey = () => 'wp6-drum';
  return m;
}
